import { Component, OnInit, ViewChild, inject } from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter, skip, switchMap, take, tap} from 'rxjs/operators';
import {Observable} from 'rxjs';

import {CsatService, FeatureFlags, FeatureFlagService, SessionService} from '@core';
import {ModalService, NotificationService} from '@shared';

import {
    BreakpointObserverService, BreakpointValue,
    NavigationService,
    ProblemInstanceService,
    TimeWindowSuggestionService
} from 'planner-shared';

import {OptimizationSettingsModalComponent} from "./optimization/settings-modal/optimization-settings-modal.component";
import {RouterOutlet} from "@angular/router";
import {OptimizationComponent} from "./optimization/optimization.component";

@UntilDestroy()
@Component({
    selector: 'app-planner',
    templateUrl: './planner.component.html',
    styleUrls: ['./planner.component.scss'],
    standalone: false
})
export class PlannerComponent implements OnInit {
    private readonly timeWindowSuggestionService = inject(TimeWindowSuggestionService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly csatService = inject(CsatService);
    private readonly navigationService = inject(NavigationService);
    private readonly sessionService = inject(SessionService);
    private readonly modalService = inject(ModalService);
    private readonly notificationService = inject(NotificationService);
    private readonly breakpointObserverService = inject(BreakpointObserverService);

    public menuItemTitles: string[];
    public currentPage: number;
    public totalPageCount: number;
    public nextButtonDisabled: boolean;
    public nextButtonHidden: boolean;
    public navigationDisabled: boolean;
    public resetButtonOnly: boolean;
    public saveAndResetButtons: boolean;
    public saveAndResetButtonsActive: boolean;
    public showCsat: boolean;
    public rideSharingEnabledOnClientSide: boolean;
    public optimizationSettingsPlacementEnabled: Observable<boolean>;
    public currentBreakpointValue: BreakpointValue;

    @ViewChild(RouterOutlet, {static: false})
    public routerOutlet!: RouterOutlet;

    protected readonly BreakpointValue = BreakpointValue;

    constructor() {
        const menuItems = this.navigationService.getMenuItems();
        this.menuItemTitles = menuItems.map(item => item.text);
    }

    ngOnInit(): void {
        this.totalPageCount = this.navigationService.getTotalPageCount();
        this.csatService
            .csatEnabled()
            .pipe(untilDestroyed(this))
            .subscribe(result => (this.showCsat = result));

        this.featureFlagService
            .init()
            .pipe(
                switchMap(() =>
                    this.featureFlagService.isEnabled(FeatureFlags.TimeWindowSuggestions).pipe(
                        filter(enabled => enabled),
                        tap(() => this.timeWindowSuggestionService.loadTimeWindowSuggestions())
                    )
                )
            )
            .subscribe();
        this.optimizationSettingsPlacementEnabled
            = this.featureFlagService.isEnabled(FeatureFlags.OptimizationSettingsPlacement);

        this.breakpointObserverService.getCurrentBreakpoint()
            .pipe(untilDestroyed(this))
            .subscribe(value => this.currentBreakpointValue = value);

        this.problemInstanceService.getProblemInstance()
            .pipe(
                take(1),
                tap(result => {
                    const planDate = result.problemInstance.officeInfo.planDate;
                    const planName = `${result.problemInstance.officeInfo.planName} ${planDate ?? ''}`;
                    const parsedPageId = Number(result.problemInstance.pageId);
                    const pageId =
                        Number.isInteger(parsedPageId) && parsedPageId >= 0 && parsedPageId < this.totalPageCount
                            ? parsedPageId : 0;

                    this.sessionService.setPlanName(planName);
                    this.navigationService.setPage(pageId);
                    this.rideSharingEnabledOnClientSide =
                        result.problemInstance.officeInfo.rideSharingEnabledOnClientSide;
                }),
                switchMap(() => this.navigationService.getCurrentPage().pipe(
                    untilDestroyed(this),
                    tap(page => {
                        this.currentPage = page;
                        this.nextButtonHidden =
                            this.navigationService.isCurrentPageOptimization() ||
                            this.navigationService.isCurrentPageRoutePlanning();
                        this.navigationService.navigate();
                    }),
                    // skipping first emit as we already set required pageId after app initializes in tap()
                    skip(1),
                    switchMap(page => this.problemInstanceService.setPageId(page))
                ))
            )
            .subscribe();

        this.navigationService
            .nextButtonDisabled()
            .pipe(untilDestroyed(this))
            .subscribe(result => (this.nextButtonDisabled = result));

        this.navigationService
            .navigationDisabled()
            .pipe(untilDestroyed(this))
            .subscribe(result => (this.navigationDisabled = result));

        this.navigationService
            .resetButtonOnly()
            .pipe(untilDestroyed(this))
            .subscribe(result => setTimeout(() => (this.resetButtonOnly = result)));

        this.navigationService
            .saveAndResetButtons()
            .pipe(untilDestroyed(this))
            .subscribe(result => setTimeout(() => (this.saveAndResetButtons = result)));

        this.navigationService
            .saveAndResetButtonsActive()
            .pipe(untilDestroyed(this))
            .subscribe(result => (this.saveAndResetButtonsActive = result));
    }

    public nextPage(): void {
        this.navigationService.nextPage();
    }

    public previousPage(): void {
        this.navigationService.previousPage();
    }

    public navigateToPage(index: number): void {
        if (index >= this.currentPage || this.navigationDisabled) {
            return;
        }
        this.navigationService.navigateToPage(index);
    }

    public saveClick(): void {
        this.navigationService.saveButtonClicked();
    }

    public resetClick(): void {
        this.navigationService.resetButtonClicked();
    }

    public openSettingsModal(): void {
        this.modalService.openCustomModal(
            OptimizationSettingsModalComponent,
            () => {
                if (this.routerOutlet.component instanceof OptimizationComponent) {
                    this.routerOutlet.component.performSettingsUpdate();
                }
                this.notificationService.success("SETTINGS_UPDATED");
            },
            null,
            null,
            true
        );
    }
}
